'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import classNames from 'classnames';
import maplibregl, { Marker } from 'maplibre-gl';
import { createRoot, Root } from 'react-dom/client';
import { MantineProvider, Popover, UnstyledButton } from '@mantine/core';
import { useSuggestedSudsContext } from '@/contexts/SuggestedSudsContext';
import { BaseMarkerConfig } from './markerconfig';
import styles from './marker.module.css';

interface MarkerContentProps {
  feature: {
    geometry: { coordinates: any; type: string };
    properties?: Record<string, any>;
  };
  config: BaseMarkerConfig;
  isFocused: boolean;
  onOpenPopover: (id: number) => void;
  openMoreDetails: (id: number) => void;
}

interface BaseMarkerProps {
  map: maplibregl.Map;
  feature: {
    geometry: { coordinates: any; type: string };
    properties?: Record<string, any>;
  };
  config: BaseMarkerConfig;
  onOpenPopover: (id: number) => void;
  openMoreDetails: (id: number) => void;
  zIndex: string;
}

export const SuggestedSudsMarkerContent = ({
  feature,
  config,
  isFocused,
  openMoreDetails,
  onOpenPopover,
}: MarkerContentProps) => {
  const [opened, setOpened] = useState(false);

  const handleMoreDetailsClicked = () => {
    setOpened(false);
    openMoreDetails(feature?.properties?.fid as number);
  };

  useEffect(() => {
    if (opened) {
      onOpenPopover(feature?.properties?.fid as number);
    }
  }, [opened, onOpenPopover]);

  const { title, details } = config.getPopoverContent(feature, handleMoreDetailsClicked);
  const icon = config.getIcon(feature);

  return (
    <MantineProvider>
      <Popover
        width={400}
        position="top"
        clickOutsideEvents={['mouseup', 'touchend']}
        offset={3}
        opened={opened}
        onChange={setOpened}
        zIndex={15}
      >
        <Popover.Target>
          <UnstyledButton
            className={classNames(
              styles.icon,
              styles.iconButton,
              styles[config.iconClass],
              styles[config.buttonClass],
              {
                [styles['iconButton-open']]: opened || isFocused,
              }
            )}
            onClick={() => setOpened((o: any) => !o)}
          >
            {icon}
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown className={styles.popover}>
          <div className={styles.popoverTitle}>
            <div className={styles.iconAndText}>
              <div className={`${styles.icon} ${styles[config.iconClass]}`}>{icon}</div>
              {title}
            </div>
            <UnstyledButton className={styles.closeButton} onClick={() => setOpened(false)}>
              <IconX aria-label="Close" />
            </UnstyledButton>
          </div>
          <hr className={styles.divider} />
          {details}
        </Popover.Dropdown>
      </Popover>
    </MantineProvider>
  );
};

export function SuggestedSudsMarkerAndPopover({
  map,
  feature,
  config,
  onOpenPopover,
  openMoreDetails,
  zIndex,
}: BaseMarkerProps) {
  const markerRef = useRef<Marker>(null);
  const rootRef = useRef<Root>(null);
  const elRef = useRef<HTMLDivElement>(null);
  const { focusedSuggestedSudsId, isSuggestedSudsDrawerOpen } = useSuggestedSudsContext();

  const isFocused = useMemo(() => {
    return (
      !!feature.properties &&
      isSuggestedSudsDrawerOpen &&
      focusedSuggestedSudsId === feature.properties.fid
    );
  }, [focusedSuggestedSudsId, isSuggestedSudsDrawerOpen]);

  useEffect(() => {
    elRef.current = document.createElement('div');
    elRef.current.style.zIndex = zIndex;
    rootRef.current = createRoot(elRef.current);

    const parsedCoordinates = feature.geometry.coordinates.map((coord: string | number) =>
      typeof coord === 'string' ? parseFloat(coord) : coord
    );
    const coordinates: [number, number] = config.getCoordinates(parsedCoordinates).slice(0, 2) as [
      number,
      number,
    ];

    markerRef.current = new maplibregl.Marker({ element: elRef.current })
      .setLngLat(coordinates)
      .addTo(map);

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
    };
  }, [map, feature]);

  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.render(
        <SuggestedSudsMarkerContent
          feature={feature}
          config={config}
          isFocused={isFocused}
          openMoreDetails={openMoreDetails}
          onOpenPopover={onOpenPopover}
        />
      );
    }
  }, [feature, config, isFocused]);

  return null;
}
